import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   TextField,
   Button,
   Alert,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogContentText,
   DialogTitle,
   Divider,
   Card,
   CardContent,
   Chip,
   Grid,
   useMediaQuery,
   useTheme,
   FormControlLabel,
   Checkbox,
   Link,
} from '@mui/material';
import {
   Phone as PhoneIcon,
   VerifiedUser as VerifiedIcon,
   Link as LinkIcon,
   LinkOff as UnlinkIcon,
   Check as CheckIcon,
} from '@mui/icons-material';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { formatPhoneNumber } from '../../../utils/smsUtils';
import { validatePhoneNumber } from '../../../services/phoneInvitationService';
import PhoneInput from '../../../components/common/PhoneInput';
import { Country, COUNTRIES } from '../../../data/countries';

interface PhoneNumberLinkingProps {
   userId: string;
}

const PhoneNumberLinking: React.FC<PhoneNumberLinkingProps> = ({ userId }) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [phoneNumber, setPhoneNumber] = useState('');
   const [selectedCountry, setSelectedCountry] = useState<Country>(
      COUNTRIES.find((c) => c.code === 'US') || COUNTRIES[0] // Defaults to United States
   );
   const [currentLinkedPhone, setCurrentLinkedPhone] = useState<string | null>(null);
   const [isPhoneVerified, setIsPhoneVerified] = useState(false);
   const [loading, setLoading] = useState(true);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   // Verification dialog state
   const [verificationOpen, setVerificationOpen] = useState(false);
   const [verificationCode, setVerificationCode] = useState('');
   const [verifying, setVerifying] = useState(false);
   const [verificationError, setVerificationError] = useState<string | null>(null);
   const [codeSent, setCodeSent] = useState(false);
   const [smsConsent, setSmsConsent] = useState(false);

   // Load current phone number
   useEffect(() => {
      const loadPhoneNumber = async () => {
         try {
            setLoading(true);
            const userDoc = await getDoc(doc(db, 'userProfiles', userId));
            if (userDoc.exists()) {
               const data = userDoc.data();
               if (data.phoneNumber) {
                  setCurrentLinkedPhone(data.phoneNumber);
                  setPhoneNumber(data.phoneNumber);
                  setIsPhoneVerified(data.phoneNumberVerified || false);
               }
            }
         } catch (err) {
            console.error('Error loading phone number:', err);
            setError('Failed to load phone number settings');
         } finally {
            setLoading(false);
         }
      };

      loadPhoneNumber();
   }, [userId]);

   // Handle phone number change
   const handlePhoneChange = (value: string) => {
      setPhoneNumber(value);
      setError(null);
      setSuccess(null);
   };

   // Send verification code
   const handleSendVerificationCode = async () => {
      try {
         setVerifying(true);
         setVerificationError(null);

         if (!phoneNumber.trim()) {
            setVerificationError('Please enter a phone number');
            setVerifying(false);
            return;
         }

         // Skip validation - let backend handle phone number format

         // Clean phone number - handle different formats based on country
         let cleanedPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove all non-digits

         // Combine country code with phone number (ensure proper format)
         const fullPhoneNumber = selectedCountry.dialCode + cleanedPhoneNumber;

         // Call backend API to send verification code
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/phone/send-verification`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  phoneNumber: fullPhoneNumber,
                  userId: userId,
                  purpose: 'phone_linking',
               }),
            }
         );

         const result = await response.json();

         if (result.success) {
            setCodeSent(true);
            setVerificationError(null);
         } else {
            setVerificationError(result.error || 'Failed to send verification code');
         }
      } catch (err) {
         console.error('Error sending verification code:', err);
         setVerificationError('An error occurred while sending verification code');
      } finally {
         setVerifying(false);
      }
   };

   // Verify code and link phone number
   const handleVerifyAndLink = async () => {
      try {
         setVerifying(true);
         setVerificationError(null);

         if (!verificationCode.trim()) {
            setVerificationError('Please enter the verification code');
            setVerifying(false);
            return;
         }

         // Clean phone number - remove leading 0 if present (common in many countries)
         let cleanedPhoneNumber = phoneNumber;
         if (cleanedPhoneNumber.startsWith('0')) {
            cleanedPhoneNumber = cleanedPhoneNumber.substring(1);
         }

         // Combine country code with phone number
         const fullPhoneNumber = selectedCountry.dialCode + cleanedPhoneNumber;

         // Call backend API to verify code
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/phone/verify-and-link`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  phoneNumber: fullPhoneNumber,
                  verificationCode: verificationCode,
                  userId: userId,
               }),
            }
         );

         const result = await response.json();

         if (result.success) {
            // Update local state
            setCurrentLinkedPhone(fullPhoneNumber);
            setIsPhoneVerified(true);
            setVerificationOpen(false);
            setSuccess('Phone number linked successfully! You can now use it to log in.');

            // Update Firestore
            await updateDoc(doc(db, 'userProfiles', userId), {
               phoneNumber: fullPhoneNumber,
               phoneNumberVerified: true,
               phoneNumberLinkedAt: new Date(),
            });
         } else {
            setVerificationError(result.error || 'Invalid verification code');
         }
      } catch (err) {
         console.error('Error verifying code:', err);
         setVerificationError('An error occurred during verification');
      } finally {
         setVerifying(false);
      }
   };

   // Unlink phone number
   const handleUnlinkPhone = async () => {
      try {
         setSaving(true);
         setError(null);

         // Call backend API to unlink phone number
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/phone/unlink`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               userId: userId,
            }),
         });

         const result = await response.json();

         if (result.success) {
            // Update local state
            setCurrentLinkedPhone(null);
            setIsPhoneVerified(false);
            setPhoneNumber('');
            setSuccess('Phone number unlinked successfully');
         } else {
            setError(result.error || 'Failed to unlink phone number');
         }
      } catch (err) {
         console.error('Error unlinking phone number:', err);
         setError('Failed to unlink phone number');
      } finally {
         setSaving(false);
      }
   };

   // Close verification dialog
   const handleCloseVerification = () => {
      setVerificationOpen(false);
      setVerificationCode('');
      setVerificationError(null);
      setCodeSent(false);
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
         </Box>
      );
   }

   return (
      <Box>
         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         {success && (
            <Alert severity='success' sx={{ mb: 2 }}>
               {success}
            </Alert>
         )}

         {/* Current Status */}
         <Card sx={{ mb: 3, bgcolor: currentLinkedPhone ? 'success.50' : 'grey.50' }}>
            <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon
                     sx={{ mr: 1, color: currentLinkedPhone ? 'success.main' : 'text.secondary' }}
                  />
                  <Typography variant='h6'>Phone Number Status</Typography>
               </Box>

               {currentLinkedPhone ? (
                  <Box>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip
                           icon={<CheckIcon />}
                           label='Linked'
                           color='success'
                           size='small'
                           sx={{ mr: 1 }}
                        />
                        {isPhoneVerified && (
                           <Chip
                              icon={<VerifiedIcon />}
                              label='Verified'
                              color='success'
                              variant='outlined'
                              size='small'
                           />
                        )}
                     </Box>
                     <Typography variant='body1' sx={{ mb: 2 }}>
                        <strong>Linked Phone:</strong> {currentLinkedPhone}
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        You can use this phone number to log into your account.
                     </Typography>
                     <Button
                        variant='outlined'
                        color='error'
                        startIcon={<UnlinkIcon />}
                        onClick={handleUnlinkPhone}
                        disabled={saving}
                        size='small'
                     >
                        {saving ? <CircularProgress size={20} /> : 'Unlink Phone Number'}
                     </Button>
                  </Box>
               ) : (
                  <Box>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        No phone number is currently linked to your account. Link a phone number to
                        enable phone-based login.
                     </Typography>
                  </Box>
               )}
            </CardContent>
         </Card>

         {/* Link New Phone Number */}
         {!currentLinkedPhone && (
            <Card>
               <CardContent>
                  <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                     <LinkIcon sx={{ mr: 1 }} />
                     Link Phone Number
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                     <Grid item xs={12}>
                        <PhoneInput
                           phoneNumber={phoneNumber}
                           selectedCountry={selectedCountry}
                           onPhoneNumberChange={handlePhoneChange}
                           onCountryChange={setSelectedCountry}
                           error={false}
                           helperText={
                              selectedCountry.code === 'NG'
                                 ? 'Enter Nigerian number (e.g., 07031234567 or 7031234567)'
                                 : selectedCountry.code === 'US'
                                   ? 'Enter US number (e.g., 5551234567)'
                                   : 'Enter the phone number you want to link to your account'
                           }
                           required={false}
                           placeholder={
                              selectedCountry.code === 'NG'
                                 ? '07031234567'
                                 : selectedCountry.code === 'US'
                                   ? '5551234567'
                                   : 'Enter phone number'
                           }
                           label='Phone Number'
                        />
                     </Grid>

                     <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                           <FormControlLabel
                              control={
                                 <Checkbox
                                    checked={smsConsent}
                                    onChange={(e) => setSmsConsent(e.target.checked)}
                                    color='primary'
                                 />
                              }
                              label={
                                 <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
                                    I consent to receive event invitations, connection requests, and
                                    platform notifications via SMS from WeTrippen. Message frequency
                                    varies. Message and data rates may apply. Reply STOP to opt-out
                                    anytime.
                                 </Typography>
                              }
                              sx={{ alignItems: 'flex-start', mb: 1 }}
                           />

                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ ml: 4, mb: 2, fontSize: '0.875rem', fontWeight: 500 }}
                           >
                              By checking this box, you agree to our{' '}
                              <Link
                                 href='/privacy-policy'
                                 target='_blank'
                                 rel='noopener noreferrer'
                                 sx={{
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                       color: 'primary.dark',
                                    },
                                 }}
                              >
                                 Privacy Policy
                              </Link>{' '}
                              and{' '}
                              <Link
                                 href='/sms-terms'
                                 target='_blank'
                                 rel='noopener noreferrer'
                                 sx={{
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                       color: 'primary.dark',
                                    },
                                 }}
                              >
                                 SMS Terms
                              </Link>
                              .
                           </Typography>

                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ ml: 4, mb: 1, fontWeight: 500 }}
                           >
                              By providing your phone number, you consent to receive:
                           </Typography>

                           <Box component='ul' sx={{ ml: 4, mb: 1, pl: 2 }}>
                              <Typography
                                 component='li'
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ mb: 0.5 }}
                              >
                                 Event invitations from your network
                              </Typography>
                              <Typography
                                 component='li'
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ mb: 0.5 }}
                              >
                                 Virtual meeting reminders
                              </Typography>
                              <Typography
                                 component='li'
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ mb: 0.5 }}
                              >
                                 New photo album notifications
                              </Typography>
                              <Typography
                                 component='li'
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ mb: 0.5 }}
                              >
                                 Connection requests
                              </Typography>
                           </Box>

                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ ml: 4, fontSize: '0.8rem' }}
                           >
                              You can manage SMS preferences or opt-out anytime in your account
                              settings or by replying STOP to any message.
                           </Typography>
                        </Box>
                     </Grid>

                     <Grid item xs={12}>
                        <Button
                           variant='contained'
                           onClick={() => setVerificationOpen(true)}
                           disabled={!phoneNumber.trim() || !smsConsent || saving}
                           startIcon={<LinkIcon />}
                           fullWidth={isMobile}
                        >
                           Link Phone Number
                        </Button>
                     </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 0.5, fontSize: '0.8rem' }}
                     >
                        <strong>Note:</strong> After linking your phone number, you can:
                     </Typography>
                     <Box component='ul' sx={{ pl: 1.5, mb: 0.5 }}>
                        <Typography
                           component='li'
                           variant='body2'
                           color='text.secondary'
                           sx={{ mb: 0.25, fontSize: '0.75rem' }}
                        >
                           Use it for phone-based login
                        </Typography>
                        <Typography
                           component='li'
                           variant='body2'
                           color='text.secondary'
                           sx={{ mb: 0.25, fontSize: '0.75rem' }}
                        >
                           Receive event invitations and updates via SMS
                        </Typography>
                        <Typography
                           component='li'
                           variant='body2'
                           color='text.secondary'
                           sx={{ mb: 0.25, fontSize: '0.75rem' }}
                        >
                           Manage SMS preferences in notification settings
                        </Typography>
                     </Box>
                     <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                        Standard messaging rates apply. Reply HELP for support, STOP to unsubscribe.
                     </Typography>
                  </Box>
               </CardContent>
            </Card>
         )}

         {/* Verification Dialog */}
         <Dialog open={verificationOpen} onClose={handleCloseVerification} maxWidth='sm' fullWidth>
            <DialogTitle>Verify Phone Number</DialogTitle>
            <DialogContent>
               <DialogContentText sx={{ mb: 2 }}>
                  {!codeSent
                     ? `We'll send a verification code to ${phoneNumber}. Please confirm to proceed.`
                     : `We've sent a verification code to ${phoneNumber}. Enter the code below to complete the linking process.`}
               </DialogContentText>

               {verificationError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {verificationError}
                  </Alert>
               )}

               {codeSent && (
                  <TextField
                     autoFocus
                     margin='dense'
                     label='Verification Code'
                     type='text'
                     fullWidth
                     variant='outlined'
                     value={verificationCode}
                     onChange={(e) => setVerificationCode(e.target.value)}
                     disabled={verifying}
                     placeholder='Enter 6-digit code'
                     inputProps={{ maxLength: 6 }}
                  />
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseVerification} disabled={verifying}>
                  Cancel
               </Button>
               {codeSent && (
                  <Button onClick={handleSendVerificationCode} disabled={verifying} variant='text'>
                     Resend Code
                  </Button>
               )}
               <Button
                  onClick={codeSent ? handleVerifyAndLink : handleSendVerificationCode}
                  disabled={verifying || (codeSent && !verificationCode.trim())}
                  variant='contained'
               >
                  {verifying ? (
                     <CircularProgress size={24} />
                  ) : codeSent ? (
                     'Verify & Link'
                  ) : (
                     'Send Code'
                  )}
               </Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default PhoneNumberLinking;
